import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionBadge from '../components/subscription/SubscriptionBadge';
import './AccountManagement.css';

/**
 * Account Management Page
 *
 * Displays user subscription details and management options
 * Allows users to upgrade, cancel, or manage their subscription
 */
export function AccountManagement() {
  const navigate = useNavigate();
  const { user, logout } = useAuth0();
  const {
    subscription,
    hasSubscription,
    isTrialing,
    daysRemaining,
    status,
    isActive,
    loading,
    startCheckout,
    openCustomerPortal,
    syncSubscription,
  } = useSubscription();

  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      await openCustomerPortal();
    } catch (err) {
      alert('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTrial = async () => {
    setIsLoading(true);
    try {
      await startCheckout('personal');
    } catch (err) {
      alert('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    setIsSyncing(true);
    try {
      await syncSubscription();
    } catch (err) {
      alert('Kon abonnement niet synchroniseren. Probeer het opnieuw.');
    } finally {
      setIsSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="account-page">
        <div className="account-loading">
          <div className="spinner"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-container">
        {/* Header */}
        <div className="account-header">
          <button
            className="account-back-btn"
            onClick={() => navigate('/app')}
            aria-label="Terug naar chat"
          >
            <span className="material-icons">arrow_back</span>
          </button>
          <h1 className="account-header__title">Mijn Account</h1>
          <SubscriptionBadge />
        </div>

        {/* User Profile */}
        <div className="account-section">
          <h2 className="account-section__title">Profiel</h2>
          <div className="account-card">
            <div className="account-field">
              <label>Naam</label>
              <p>{user?.name || 'Niet ingesteld'}</p>
            </div>
            <div className="account-field">
              <label>E-mail</label>
              <p>{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        <div className="account-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 className="account-section__title" style={{ marginBottom: 0 }}>Abonnement</h2>
            {hasSubscription && (
              <button
                className="account-btn account-btn--secondary"
                onClick={handleSyncSubscription}
                disabled={isSyncing}
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                {isSyncing ? '🔄 Synchroniseren...' : '🔄 Vernieuw Status'}
              </button>
            )}
          </div>

          {!hasSubscription ? (
            <div className="account-card account-card--cta">
              <div className="account-empty">
                <span className="account-empty__icon">💎</span>
                <h3>Geen actief abonnement</h3>
                <p>Start je 7-dagen gratis trial en ontdek alle premium functies</p>
                <button
                  className="account-btn account-btn--primary"
                  onClick={handleStartTrial}
                  disabled={isLoading}
                >
                  {isLoading ? 'Laden...' : 'Start Gratis Trial'}
                </button>
              </div>
            </div>
          ) : (
            <div className="account-card">
              <div className="account-subscription">
                <div className="account-field">
                  <label>Plan</label>
                  <p className="account-field--highlight">Personal Plan</p>
                </div>

                <div className="account-field">
                  <label>Prijs</label>
                  <p>€49 per maand</p>
                </div>

                <div className="account-field">
                  <label>Status</label>
                  <p>
                    <span className={`account-status account-status--${status}`}>
                      {status === 'trialing' && '🎯 Trial actief'}
                      {status === 'active' && '✅ Actief'}
                      {status === 'past_due' && '⚠️ Betaling mislukt'}
                      {status === 'canceled' && '❌ Geannuleerd'}
                      {status === 'paused' && '⏸️ Gepauzeerd'}
                    </span>
                  </p>
                </div>

                {subscription?.created_at && (
                  <div className="account-field">
                    <label>Abonnement gestart</label>
                    <p>{formatDate(subscription.created_at)}</p>
                  </div>
                )}

                {isTrialing && (
                  <>
                    {subscription?.trial_start && (
                      <div className="account-field">
                        <label>Trial gestart</label>
                        <p>{formatDate(subscription.trial_start)}</p>
                      </div>
                    )}
                    <div className="account-field">
                      <label>Trial eindigt</label>
                      <p>{formatDate(subscription?.trial_end)}</p>
                    </div>
                    <div className="account-field">
                      <label>Resterende dagen</label>
                      <p className="account-field--highlight">
                        {daysRemaining} {daysRemaining === 1 ? 'dag' : 'dagen'}
                      </p>
                    </div>
                    {subscription?.auto_trial && (
                      <div className="account-field account-field--info">
                        <label>ℹ️ Gratis Trial</label>
                        <p>
                          Je gebruikt de gratis trial. Upgrade naar het betaalde plan
                          om toegang te behouden na de trial periode.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {isActive && !isTrialing && (
                  <>
                    {subscription?.current_period_start && (
                      <div className="account-field">
                        <label>Huidige periode gestart</label>
                        <p>{formatDate(subscription.current_period_start)}</p>
                      </div>
                    )}
                    <div className="account-field">
                      <label>Volgende factuurdatum</label>
                      <p>{formatDate(subscription?.current_period_end)}</p>
                    </div>
                  </>
                )}

                <div className="account-actions">
                  {subscription?.auto_trial ? (
                    <>
                      <button
                        className="account-btn account-btn--primary"
                        onClick={handleStartTrial}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Laden...' : '💳 Upgrade Naar Betaald Plan'}
                      </button>
                      <p className="account-note" style={{ marginTop: '8px', fontSize: '13px' }}>
                        Na het invullen van betaalgegevens blijft je trial actief tot het einde.
                      </p>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <button
                        className="account-btn account-btn--secondary"
                        onClick={handleManageSubscription}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Laden...' : 'Beheer Abonnement'}
                      </button>
                      <a
                        href="https://billing.stripe.com/p/login/28EeVfcol3Z2bD4af76kg00"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="account-btn account-btn--outline"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          textDecoration: 'none',
                        }}
                      >
                        <span>📋</span> Bekijk Facturen & Betaalhistorie
                      </a>
                    </div>
                  )}
                </div>

                <p className="account-note">
                  Via het abonnement portaal kun je je betaalgegevens aanpassen,
                  facturen bekijken en je abonnement opzeggen.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Enterprise CTA */}
        <div className="account-section">
          <div className="account-card account-card--enterprise">
            <div className="account-enterprise">
              <h3>🏢 Enterprise Plan</h3>
              <p>
                Voor grote teams en organisaties bieden wij maatwerkoplossingen
                met aangepaste facturering, dedicated support en volume kortingen.
              </p>
              <a
                href="mailto:pim.südmeier@humanaize.nl?subject=Enterprise Plan - Jesse"
                className="account-btn account-btn--outline"
              >
                Neem Contact Op
              </a>
            </div>
          </div>
        </div>

        {/* Logout */}
        <div className="account-section">
          <button
            className="account-btn account-btn--danger"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Uitloggen
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountManagement;
